/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { memo } from 'react';

import { Tag, VisuallyHidden, typography2 } from '@coursera/cds-core';

export type ChipButtonProps = {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  children: React.ReactNode;
  isActive?: boolean;
  isDisabled?: boolean;
  showLabel?: boolean;
  count?: number;
  showCount?: boolean;
};

const styles = {
  chipButton: css`
    ${typography2.actionSecondary}
    align-items: center;
    display: flex;
    border-radius: 16px;
    padding: 0 12px;
    margin: 3px;
    height: 32px;
    gap: 5px;
    font-size: 14px;
    border: 1px solid var(--cds-color-neutral-stroke-primary);
    background-color: var(--cds-color-white-0);
    outline-offset: 3px;
    outline-width: 1px;

    &:hover,
    &:focus {
      background-color: var(--cds-color-grey-25);
    }

    &:active {
      border-color: var(--cds-color-grey-975);
    }

    .prefix,
    .suffix {
      height: 16px;

      svg {
        height: 16px;
        width: 16px;
      }
    }

    &:disabled {
      background-color: var(--cds-color-white-0);
      border: 1px solid var(--cds-color-neutral-disabled-strong);
      color: var(--cds-color-neutral-disabled-strong);

      &:hover,
      &:focus {
        background-color: var(--cds-color-white-0);
        border: 1px solid var(--cds-color-neutral-disabled-strong);
        color: var(--cds-color-neutral-disabled-strong);
      }
    }
  `,

  circleButton: css`
    .prefix {
      margin-left: -5px;
      margin-right: -5px;
    }
  `,

  isSelected: css`
    background-color: var(--cds-color-grey-975);
    border: 1px solid var(--cds-color-grey-975);
    color: var(--cds-color-white-0);

    &:hover,
    &:focus {
      background-color: var(--cds-color-grey-975);
      color: var(--cds-color-white-0);
    }

    &:disabled {
      background-color: var(--cds-color-neutral-disabled-strong);
      border: 1px solid var(--cds-color-neutral-disabled-strong);
      color: var(--cds-color-white-0);

      &:hover,
      &:focus {
        background-color: var(--cds-color-neutral-disabled-strong);
        border: 1px solid var(--cds-color-neutral-disabled-strong);
        color: var(--cds-color-white-0);
      }
    }
  `,

  customCountStyles: css`
    padding: 0 6px;
  `,

  customInactiveCountStyles: css`
    background-color: var(--cds-color-grey-50) !important;
    color: #404b61 !important;
    border-color: var(--cds-color-grey-50) !important;
  `,
  chipLabel: css`
    white-space: nowrap;
  `,
};

export enum CHIP_BUTTON_STATES {
  ACTIVE = 'ACTIVE',
  DISABLED = 'DISABLED',
  DISABLED_ACTIVE = 'DISABLED_ACTIVE',
  INACTIVE = 'INACTIVE',
}

const CHIP_BUTTON_STATE_CONFIGS = {
  [`${CHIP_BUTTON_STATES.ACTIVE}`]: {
    testId: 'chip-button-active',
  },
  [`${CHIP_BUTTON_STATES.DISABLED}`]: {
    testId: 'chip-button-disabled',
  },
  [`${CHIP_BUTTON_STATES.DISABLED_ACTIVE}`]: {
    testId: 'chip-button-disabled-active',
  },
  [`${CHIP_BUTTON_STATES.INACTIVE}`]: {
    testId: 'chip-button-inactive',
  },
} as const;

export const getInitialState = (isActive?: boolean, isDisabled?: boolean) => {
  if (isActive && !isDisabled) {
    return CHIP_BUTTON_STATES.ACTIVE;
  } else if (!isActive && isDisabled) {
    return CHIP_BUTTON_STATES.DISABLED;
  } else if (isActive && isDisabled) {
    return CHIP_BUTTON_STATES.DISABLED_ACTIVE;
  } else {
    return CHIP_BUTTON_STATES.INACTIVE;
  }
};

export const ChipButton = memo(
  ({
    onClick,
    prefix,
    isActive,
    isDisabled,
    suffix,
    showLabel = true,
    showCount = true,
    count,
    children,
  }: ChipButtonProps) => {
    const chipButtonState = getInitialState(isActive, isDisabled);
    const wrappedChildren = <div css={styles.chipLabel}>{children}</div>;

    return (
      <button
        onClick={onClick}
        css={[styles.chipButton, isActive && styles.isSelected, !showLabel && !showCount && styles.circleButton]}
        type="button"
        data-testid={CHIP_BUTTON_STATE_CONFIGS[chipButtonState].testId}
        disabled={isDisabled}
      >
        {prefix && <div className="prefix">{prefix}</div>}
        {showLabel ? wrappedChildren : <VisuallyHidden>{wrappedChildren}</VisuallyHidden>}
        {count !== undefined && showCount && (
          <Tag variant="status" css={[styles.customCountStyles, !isActive && styles.customInactiveCountStyles]}>
            {count}
          </Tag>
        )}
        {suffix && <div className="suffix">{suffix}</div>}
      </button>
    );
  }
);

export default ChipButton;
